import { useId, useState } from "react";

import type { AttributeReviewValueFixInput } from "@/graphql/generated";
import { valueLook } from "@/lib/attributeReview/valueLook";
import {
  poolChoices,
  type PoolChoice,
  type PoolValue,
} from "@/lib/attributeReview/valueView";
import { VALUE_GRID, ValueMapRow } from "./ValueMapRow";

type Props = {
  values: PoolValue[];
  poolValues: PoolChoice[];
  fixes: Record<string, AttributeReviewValueFixInput>;
  setFix: (fix: AttributeReviewValueFixInput) => void;
  undoFix: (storeValue: string) => void;
  readOnly: boolean;
  storeName: string;
};

export function ValueMapTable({
  values,
  poolValues,
  fixes,
  setFix,
  undoFix,
  readOnly,
  storeName,
}: Props) {
  const listId = useId();
  const [showExact, setShowExact] = useState(false);
  if (values.length === 0) return null;
  const choices = poolChoices(poolValues, values, fixes);
  const looks = values.map((v) => valueLook(v, fixes[v.store_value], choices));
  const settled = values.filter((_, i) => readOnly || !looks[i].visible);
  const open = values.filter((_, i) => !readOnly && looks[i].visible);
  const row = (v: PoolValue) => (
    <ValueMapRow
      key={v.store_value}
      value={v}
      fix={fixes[v.store_value]}
      choices={choices}
      listId={listId}
      setFix={setFix}
      undoFix={undoFix}
    />
  );
  return (
    <section className="flex flex-col gap-2" aria-label="Values">
      {open.length > 0 && (
        <>
          <div
            className={`${VALUE_GRID} px-2.5 text-xs text-slate-500 dark:text-slate-400`}
          >
            <span>{storeName} sends</span>
            <span>We save as</span>
            <span />
          </div>
          <div>{open.map(row)}</div>
        </>
      )}
      {settled.length > 0 && (
        <div className="text-xs text-slate-500 dark:text-slate-400">
          {!readOnly && (
            <button
              type="button"
              aria-expanded={showExact}
              className="rounded px-2.5 font-medium text-indigo-600 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 dark:text-indigo-300"
              onClick={() => setShowExact((on) => !on)}
            >
              {showExact
                ? "Hide exact matches"
                : `Show ${settled.length} exact matches`}
            </button>
          )}
          {!readOnly && showExact && (
            <div className="mt-1">{settled.map(row)}</div>
          )}
          {readOnly && (
            <ul className="mt-1 space-y-0.5 opacity-80">
              {settled.map((v) => (
                <li key={v.store_value} className="flex gap-3">
                  <span className="min-w-0 flex-1 truncate font-mono">
                    {v.store_label}
                  </span>
                  <span className="min-w-0 flex-1 truncate">
                    {valueLook(v, fixes[v.store_value], choices).row.name ||
                      "—"}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
      <datalist id={listId}>
        {choices.map((c) => (
          <option key={c.key} value={c.label} />
        ))}
      </datalist>
    </section>
  );
}
